<template>
  <div class="b44-page">
    <div class="b44-header">
      <div>
        <h1 class="b44-title bold">Gestão de Projetos</h1>
        <p class="b44-sub">Metodologia de Ritmo e Tração — Score de Prioridade e Saúde do Portfólio</p>
        <div v-if="!loading" class="b44-chips">
          <button
            v-for="imp in impactos"
            :key="imp.valor"
            type="button"
            class="b44-chip clicavel"
            :class="[imp.tone, { active: impactoFiltro === imp.valor, dimmed: impactoFiltro !== null && impactoFiltro !== imp.valor }]"
            :title="`Ver somente projetos ${imp.label.toLowerCase()}`"
            @click="alternarImpacto(imp.valor)"
          >
            <strong>{{ countsImpacto[imp.valor] || 0 }}</strong>
            <span>{{ imp.label }}</span>
          </button>
        </div>
      </div>
      <DocLink from="portfolio" />
    </div>

    <div v-if="loading" class="b44-loading"><div class="b44-spinner"></div></div>
    <div v-else class="b44-stack" style="margin-top:1.5rem">
      <div class="b44-card" style="padding:1.25rem">
        <div class="b44-bloco-head">
          <i class="bi bi-folder2-open" style="color:var(--b44-primary)"></i>
          <h2 class="b44-h2">Resumo do Portfólio <HelpTip tip="port_resumo" /></h2>
          <span class="spacer" style="font-size:1.5rem;font-weight:700;font-variant-numeric:tabular-nums">{{ projetosNoImpacto.length }}</span>
          <span class="b44-muted" style="font-size:0.875rem">{{ impactoAtivo ? `projetos ${impactoAtivo.label.toLowerCase()}` : "projetos no total" }}</span>
        </div>
        <div class="b44-status-grid">
          <button
            v-for="st in statusCards"
            :key="st.key"
            type="button"
            class="b44-status-card"
            :class="[st.key, { active: statusFilter === st.key }]"
            @click="statusFilter = st.key"
          >
            <div class="top">
              <span class="dot"></span>
              <span class="n">{{ countsPorStatus[st.key] || 0 }}</span>
            </div>
            <p class="l">{{ st.label }}</p>
          </button>
        </div>

        <div class="b44-grid-3" style="margin-top:1.25rem">
          <div class="b44-fin-card ativos">
            <div class="b44-fin-head">
              <i class="bi bi-graph-up-arrow"></i>
              <h3>Projetos Ativos <HelpTip tip="port_fin_ativos" /></h3>
            </div>
            <p class="b44-fin-main">{{ fmtMoeda(valorAtivo) }}</p>
            <div class="b44-fin-rows">
              <div v-for="st in statusAtivosKeys" :key="'va-' + st" class="b44-fin-row">
                <span>{{ statusLabels[st] }}</span>
                <span class="v">{{ fmtMoeda(valorPorStatus[st]) }}</span>
              </div>
              <div class="b44-fin-row foot">
                <span>Valor Consumido</span>
                <span class="v">{{ fmtMoeda(custoAtivo) }}</span>
              </div>
            </div>
          </div>

          <div class="b44-fin-card inativos">
            <div class="b44-fin-head">
              <i class="bi bi-activity"></i>
              <h3>Projetos Inativos <HelpTip tip="port_fin_inativos" /></h3>
            </div>
            <p class="b44-fin-main">{{ fmtMoeda(valorInativo) }}</p>
            <div class="b44-fin-rows">
              <div v-for="st in statusInativosKeys" :key="'vi-' + st" class="b44-fin-row">
                <span>{{ statusLabels[st] }}</span>
                <span class="v">{{ fmtMoeda(valorPorStatus[st]) }}</span>
              </div>
              <div class="b44-fin-row foot">
                <span>Valor Consumido</span>
                <span class="v">{{ fmtMoeda(custoInativo) }}</span>
              </div>
            </div>
          </div>

          <div class="b44-fin-card total">
            <div class="b44-fin-head">
              <i class="bi bi-currency-dollar"></i>
              <h3>Valor Total do Portfólio <HelpTip tip="port_fin_total" /></h3>
            </div>
            <p class="b44-fin-main">{{ fmtMoeda(valorAtivo + valorInativo) }}</p>
            <div class="b44-fin-rows">
              <div class="b44-fin-row">
                <span>Ativos ({{ countAtivos }})</span>
                <span class="v ativos">{{ fmtMoeda(valorAtivo) }}</span>
              </div>
              <div class="b44-fin-row">
                <span>Inativos ({{ countInativos }})</span>
                <span class="v inativos">{{ fmtMoeda(valorInativo) }}</span>
              </div>
              <div class="b44-fin-row foot">
                <span>Valor Consumido Total</span>
                <span class="v">{{ fmtMoeda(custoAtivo + custoInativo) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div>
        <div class="b44-bloco-head" style="margin-bottom:0.75rem">
          <i class="bi bi-kanban" style="color:var(--b44-primary)"></i>
          <h2 class="b44-h2">Matriz de Decisão <HelpTip tip="port_matriz" /></h2>
        </div>
        <div class="b44-grid-6">
          <button
            v-for="q in quadrantes"
            :key="q.key"
            type="button"
            class="b44-quad lg"
            :class="[q.tone, { active: filtro === q.key }]"
            @click="filtro = filtro === q.key ? 'todos' : q.key"
          >
            <div class="b44-quad-top">
              <i class="b44-quad-icon" :class="q.icon"></i>
              <span class="b44-quad-count" :class="q.tone">{{ countsMatriz[q.key] || 0 }}</span>
            </div>
            <div class="b44-quad-label">{{ q.label }}</div>
            <div class="b44-quad-desc">{{ q.desc }}</div>
          </button>
        </div>
      </div>

      <div class="b44-filtros">
        <div style="position:relative;display:flex;align-items:center;flex:1;min-width:240px">
          <i class="bi bi-search" style="position:absolute;left:0.65rem;color:#64748b;font-size:0.9rem;pointer-events:none"></i>
          <input
            v-model="busca"
            type="search"
            placeholder="Buscar por nome, código, descrição, pacotes de trabalho ou tarefas..."
            style="padding-left:2rem;width:100%"
          />
        </div>
        <div style="display:flex;align-items:center;gap:0.5rem">
          <i class="bi bi-funnel" style="color:#64748b"></i>
          <select v-model="statusFilter" style="min-width:180px">
            <option value="vigentes">Vigentes (ativos)</option>
            <option value="todos">Todos os status</option>
            <option v-for="st in statusCards" :key="'opt-' + st.key" :value="st.key">{{ st.label }}</option>
          </select>
        </div>
        <span style="width:1px;height:1.5rem;background:#e2e8f0"></span>
        <button v-if="filtro !== 'todos'" type="button" class="b44-btn" @click="filtro = 'todos'">Ver todos os quadrantes</button>
        <span v-else class="b44-muted" style="font-size:0.875rem">Clique num quadrante para filtrar</span>
        <span class="b44-muted" style="font-size:0.875rem;margin-left:auto">{{ projetosFiltrados.length }} projeto(s) listado(s)</span>
      </div>

      <div class="b44-table-wrap">
        <table class="b44-table">
          <thead>
            <tr>
              <th class="align-left" @click="handleSort('nome')">Projeto <HelpTip tip="col_projeto" /> {{ sortMark('nome') }}</th>
              <th @click="handleSort('nome_gerente')">Gerente <HelpTip tip="col_gerente" /> {{ sortMark('nome_gerente') }}</th>
              <th @click="handleSort('status')">Status <HelpTip tip="col_status" /> {{ sortMark('status') }}</th>
              <th @click="handleSort('saude')">Saúde <HelpTip tip="col_saude" /> {{ sortMark('saude') }}</th>
              <th @click="handleSort('nota_final')" style="text-align:right">Nota Final <HelpTip tip="col_nota_final" /> {{ sortMark('nota_final') }}</th>
              <th @click="handleSort('matriz')">Matriz (P×E) <HelpTip tip="col_matriz" /> {{ sortMark('matriz') }}</th>
              <th @click="handleSort('entregas')" style="text-align:right">Pacotes de trabalho <HelpTip tip="col_entregas" /> {{ sortMark('entregas') }}</th>
              <th @click="handleSort('tarefas')" style="text-align:right">Tarefas <HelpTip tip="col_tarefas" /> {{ sortMark('tarefas') }}</th>
              <th @click="handleSort('dias_desatualizado')" style="text-align:right">Desatualizado <HelpTip tip="col_desatualizado" /> {{ sortMark('dias_desatualizado') }}</th>
              <th @click="handleSort('orcamento')" style="text-align:right">Orçamento <HelpTip tip="col_orcamento" /> {{ sortMark('orcamento') }}</th>
              <th @click="handleSort('alertas')" style="text-align:center">Alertas <HelpTip tip="col_alertas" /> {{ sortMark('alertas') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="p in projetosFiltrados" :key="p.id">
              <td class="align-left">
                <router-link :to="{ name: 'portfolioProjetoDetalheAuditor', params: { id: p.id } }">
                  <span v-if="p.codigo" style="font-family:ui-monospace,monospace;font-size:0.7rem;color:#64748b;margin-right:0.35rem">{{ p.codigo }}</span>
                  <span style="font-weight:500">{{ p.nome }}</span>
                </router-link>
                <div v-if="p.nome_programa" style="font-size:0.7rem;color:#64748b">{{ p.nome_programa }}</div>
              </td>
              <td style="color:#64748b">{{ p.nome_gerente || "—" }}</td>
              <td><span style="font-size:0.75rem;color:#64748b">{{ statusLabels[p.status] || p.status }}</span></td>
              <td><HealthBadge :saude="p.saude" /></td>
              <td style="text-align:right">
                <span
                  class="b44-nota-final"
                  :class="toneNotaFinal(p)"
                  title="Média das notas de planejamento e execução (0–100)"
                >
                  {{ notaFinalTexto(p) }}
                </span>
              </td>
              <td>
                <div style="display:flex;align-items:center;gap:0.5rem">
                  <span style="font-family:ui-monospace,monospace;font-size:0.75rem;font-weight:600;font-variant-numeric:tabular-nums">{{ p.score_planejamento ?? 0 }}×{{ p.score_execucao ?? 0 }}</span>
                  <MatrizBadge :classificacao="p.classificacao_matriz" />
                </div>
              </td>
              <td style="text-align:right;color:#64748b">
                {{ p.total_entregas > 0 ? `${p.entregas_concluidas || 0}/${p.total_entregas}` : "—" }}
              </td>
              <td style="text-align:right;color:#64748b">
                {{ p.total_tarefas > 0 ? `${p.tarefas_concluidas || 0}/${p.total_tarefas}` : "—" }}
              </td>
              <td style="text-align:right">
                <span
                  :style="{
                    fontSize: '0.75rem',
                    fontWeight: (p.dias_desatualizado || 0) > 15 ? 500 : 400,
                    color: (p.dias_desatualizado || 0) > 30 ? '#dc2626' : (p.dias_desatualizado || 0) > 15 ? '#d97706' : '#64748b',
                  }"
                >
                  {{ p.dias_desatualizado ?? 0 }} dias
                </span>
              </td>
              <td style="text-align:right">
                <template v-if="(p.orcamento || 0) > 0 || (p.custo_real || 0) > 0">
                  <div v-if="(p.orcamento || 0) > 0" style="font-size:0.75rem;color:#64748b">{{ fmtMoeda(p.orcamento) }}</div>
                  <div v-if="(p.custo_real || 0) > 0" style="font-size:0.75rem;font-weight:500" :style="{ color: p.estourou_custo ? '#dc2626' : '#64748b' }">
                    {{ fmtMoeda(p.custo_real) }}<template v-if="p.estourou_custo"> ({{ p.percentual_custo }}%)</template>
                  </div>
                </template>
                <span v-else style="color:#64748b">—</span>
              </td>
              <td>
                <div style="display:flex;align-items:center;justify-content:center;gap:0.35rem;flex-wrap:wrap">
                  <span v-if="p.atrasado" class="b44-chip-alerta red" :title="`Projeto atrasado há ${p.dias_atraso} dias`">
                    <i class="bi bi-clock"></i> {{ p.dias_atraso }}d
                  </span>
                  <span v-if="(p.entregas_atrasadas || 0) > 0" class="b44-chip-alerta orange" :title="`${p.entregas_atrasadas} pacotes de trabalho atrasados`">
                    <i class="bi bi-exclamation-triangle"></i> {{ p.entregas_atrasadas }}E
                  </span>
                  <span v-if="(p.tarefas_atrasadas || 0) > 0" class="b44-chip-alerta amber" :title="`${p.tarefas_atrasadas} tarefas atrasadas`">
                    <i class="bi bi-exclamation-triangle"></i> {{ p.tarefas_atrasadas }}T
                  </span>
                  <span v-if="p.estourou_custo" class="b44-chip-alerta red" :title="`Custo estourou em ${p.percentual_custo}% do orçamento`">
                    <i class="bi bi-currency-dollar"></i> {{ p.percentual_custo }}%
                  </span>
                  <i
                    v-if="!p.atrasado && !(p.entregas_atrasadas || 0) && !(p.tarefas_atrasadas || 0) && !p.estourou_custo"
                    class="bi bi-check-circle-fill b44-ok-icon"
                  ></i>
                </div>
              </td>
            </tr>
            <tr v-if="!projetosFiltrados.length">
              <td colspan="11" style="text-align:center;padding:3rem;color:#64748b">
                <i class="bi bi-folder2-open" style="display:block;font-size:2rem;opacity:0.4;margin-bottom:0.5rem"></i>
                Nenhum projeto encontrado.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import "@/assets/css/portfolio-auditor.css";
import servicePortfolioProjetos from "@/services/servicePortfolioProjetos";
import { getPermissao } from "@/services/permissao-service";
import {
  statusLabels,
  statusVigentes,
  statusGestao,
  statusAtivos,
  statusInativos,
  fmtMoeda,
} from "@/utils/roboflexLabels";
import HealthBadge from "@/components/PortfolioAuditor/HealthBadge.vue";
import MatrizBadge from "@/components/PortfolioAuditor/MatrizBadge.vue";
import HelpTip from "@/components/PortfolioAuditor/HelpTip.vue";
import DocLink from "@/components/PortfolioAuditor/DocLink.vue";
import { useApiError } from "@/composables/useApiError";
export default {
  name: "PortfolioProjetosAuditor",
  components: { HealthBadge, MatrizBadge, HelpTip, DocLink },
  setup() {
    const { handleError } = useApiError();
    return { handleApiError: handleError, fmtMoeda, statusLabels };
  },
  data() {
    return {
      loading: true,
      todosProjetos: [],
      filtro: "todos",
      statusFilter: "vigentes",
      impactoFiltro: null,
      busca: "",
      sortKey: "nota_geral",
      sortDir: "desc",
      statusAtivosKeys: statusAtivos,
      statusInativosKeys: statusInativos,
      statusCards: [
        { key: "em_andamento", label: "Em Andamento" },
        { key: "nao_iniciado", label: "Não Iniciado" },
        { key: "aprovado", label: "Aprovado" },
        { key: "concluido", label: "Concluído" },
        { key: "suspenso", label: "Suspenso" },
        { key: "cancelado", label: "Cancelado" },
      ],
      impactos: [
        { valor: 5, label: "Estratégicos", tone: "estrategico" },
        { valor: 3, label: "Táticos", tone: "tatico" },
        { valor: 1, label: "Operacionais", tone: "operacional" },
      ],
      quadrantes: [
        { key: "intervir", label: "Intervir", desc: "Primeiro da pauta", tone: "red", icon: "bi bi-exclamation-octagon" },
        { key: "agir", label: "Agir", desc: "Monitorar de perto", tone: "amber", icon: "bi bi-lightning-charge" },
        { key: "ideal", label: "Ideal", desc: "Reconhecer desempenho", tone: "green", icon: "bi bi-check-circle" },
        { key: "apoiar", label: "Apoiar", desc: "Melhorar plano e exec.", tone: "blue", icon: "bi bi-shield-check" },
        { key: "questionar", label: "Questionar", desc: "Investigar anomalia", tone: "amber", icon: "bi bi-question-circle" },
        { key: "cancelar", label: "Cancelar", desc: "Candidato a cancelar", tone: "slate", icon: "bi bi-slash-circle" },
      ],
    };
  },
  computed: {
    impactoAtivo() {
      return this.impactos.find((i) => i.valor === this.impactoFiltro) || null;
    },
    /** Recorte de impacto aplicado a toda a tela (resumo, matriz e tabela). */
    projetosNoImpacto() {
      if (this.impactoFiltro === null) return this.todosProjetos;
      return this.todosProjetos.filter((p) => Number(p.impacto_estrategico ?? 1) === this.impactoFiltro);
    },
    projetosGestao() {
      return this.projetosNoImpacto.filter((p) => statusGestao.includes(p.status));
    },
    countsMatriz() {
      const c = { intervir: 0, agir: 0, ideal: 0, apoiar: 0, questionar: 0, cancelar: 0 };
      for (const p of this.projetosGestao) {
        if (c[p.classificacao_matriz] != null) c[p.classificacao_matriz]++;
      }
      return c;
    },
    countsPorStatus() {
      const c = {};
      for (const st of this.statusCards) c[st.key] = 0;
      for (const p of this.projetosNoImpacto) {
        if (c[p.status] != null) c[p.status]++;
      }
      return c;
    },
    valorPorStatus() {
      const v = {};
      for (const st of this.statusCards) v[st.key] = 0;
      for (const p of this.projetosNoImpacto) {
        if (v[p.status] != null) v[p.status] += p.orcamento || 0;
      }
      return v;
    },
    custoPorStatus() {
      const v = {};
      for (const st of this.statusCards) v[st.key] = 0;
      for (const p of this.projetosNoImpacto) {
        if (v[p.status] != null) v[p.status] += p.custo_real || 0;
      }
      return v;
    },
    valorAtivo() {
      return statusAtivos.reduce((s, st) => s + (this.valorPorStatus[st] || 0), 0);
    },
    valorInativo() {
      return statusInativos.reduce((s, st) => s + (this.valorPorStatus[st] || 0), 0);
    },
    custoAtivo() {
      return statusAtivos.reduce((s, st) => s + (this.custoPorStatus[st] || 0), 0);
    },
    custoInativo() {
      return statusInativos.reduce((s, st) => s + (this.custoPorStatus[st] || 0), 0);
    },
    countAtivos() {
      return statusAtivos.reduce((s, st) => s + (this.countsPorStatus[st] || 0), 0);
    },
    countInativos() {
      return statusInativos.reduce((s, st) => s + (this.countsPorStatus[st] || 0), 0);
    },
    /** Base dos filtros de status, matriz e busca — sem o recorte de impacto. */
    projetosAntesDoImpacto() {
      let list = this.todosProjetos;
      if (this.statusFilter === "vigentes") {
        list = list.filter((p) => statusVigentes.includes(p.status));
      } else if (this.statusFilter !== "todos") {
        list = list.filter((p) => p.status === this.statusFilter);
      }
      if (this.filtro !== "todos") {
        list = list.filter((p) => p.classificacao_matriz === this.filtro);
      }
      const termo = this.busca.trim().toLowerCase();
      if (termo) {
        list = list.filter((p) => {
          const hay = `${p.nome || ""} ${p.codigo || ""} ${p.descricao || ""} ${p.busca_entregas || ""}`.toLowerCase();
          return hay.includes(termo);
        });
      }
      return list;
    },
    countsImpacto() {
      const c = { 5: 0, 3: 0, 1: 0 };
      for (const p of this.projetosAntesDoImpacto) {
        const nivel = Number(p.impacto_estrategico ?? 1);
        if (c[nivel] != null) c[nivel]++;
      }
      return c;
    },
    projetosFiltrados() {
      let list = this.projetosAntesDoImpacto;
      if (this.impactoFiltro !== null) {
        list = list.filter((p) => Number(p.impacto_estrategico ?? 1) === this.impactoFiltro);
      }
      const dir = this.sortDir === "asc" ? 1 : -1;
      return [...list].sort((a, b) => {
        let av, bv;
        switch (this.sortKey) {
          case "nome":
            av = (a.nome || "").toLowerCase();
            bv = (b.nome || "").toLowerCase();
            break;
          case "nome_gerente":
            av = (a.nome_gerente || "").toLowerCase();
            bv = (b.nome_gerente || "").toLowerCase();
            break;
          case "status":
            av = a.status || "";
            bv = b.status || "";
            break;
          case "saude":
            av = a.saude || "";
            bv = b.saude || "";
            break;
          case "nota_geral":
            av = a.nota_geral ?? 0;
            bv = b.nota_geral ?? 0;
            break;
          case "nota_final":
            av = this.notaFinal(a);
            bv = this.notaFinal(b);
            break;
          case "matriz":
            av = (a.score_planejamento ?? 0) * 10 + (a.score_execucao ?? 0);
            bv = (b.score_planejamento ?? 0) * 10 + (b.score_execucao ?? 0);
            break;
          case "entregas":
            av = a.total_entregas ?? 0;
            bv = b.total_entregas ?? 0;
            break;
          case "tarefas":
            av = a.total_tarefas ?? 0;
            bv = b.total_tarefas ?? 0;
            break;
          case "dias_desatualizado":
            av = a.dias_desatualizado ?? 0;
            bv = b.dias_desatualizado ?? 0;
            break;
          case "orcamento":
            av = a.orcamento ?? 0;
            bv = b.orcamento ?? 0;
            break;
          case "alertas":
            av = (a.atrasado ? 1 : 0) + (a.entregas_atrasadas || 0) + (a.tarefas_atrasadas || 0) + (a.estourou_custo ? 1 : 0);
            bv = (b.atrasado ? 1 : 0) + (b.entregas_atrasadas || 0) + (b.tarefas_atrasadas || 0) + (b.estourou_custo ? 1 : 0);
            break;
          default:
            av = a.nota_geral ?? 0;
            bv = b.nota_geral ?? 0;
        }
        if (typeof av === "string" && typeof bv === "string") return av.localeCompare(bv, "pt-BR") * dir;
        return (av - bv) * dir;
      });
    },
  },
  async created() {
    const funcs = await getPermissao();
    if (!funcs.includes(116)) {
      this.$router.replace("/");
      return;
    }
    if (this.$route.query.matriz) {
      this.filtro = String(this.$route.query.matriz);
    }
    const impacto = Number(this.$route.query.impacto);
    if ([5, 3, 1].includes(impacto)) {
      this.impactoFiltro = impacto;
    }
    try {
      const data = await servicePortfolioProjetos.listar({ status: "todos" });
      this.todosProjetos = data.data || [];
    } catch (e) {
      this.handleApiError(e);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    alternarImpacto(valor) {
      this.impactoFiltro = this.impactoFiltro === valor ? null : valor;
    },
    handleSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === "asc" ? "desc" : "asc";
      } else {
        this.sortKey = key;
        this.sortDir = key === "nome" || key === "nome_gerente" ? "asc" : "desc";
      }
    },
    sortMark(key) {
      if (this.sortKey !== key) return "";
      return this.sortDir === "asc" ? "↑" : "↓";
    },
    notaFinal(p) {
      return (((Number(p.score_planejamento) || 0) + (Number(p.score_execucao) || 0)) / 2) * 10;
    },
    notaFinalTexto(p) {
      return String(Math.round(this.notaFinal(p)));
    },
    toneNotaFinal(p) {
      const nota = this.notaFinal(p);
      if (nota >= 70) return "ok";
      if (nota >= 50) return "warn";
      return "bad";
    },
  },
};
</script>
